import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LayoutGrid,
    LineChart as LineIcon,
    BarChart3 as BarIcon,
    PieChart as PieIcon,
    Radar,
    Layers,
    Table2,
    Search,
    Menu,
    X,
    Save,
    Loader2,
    Database,
} from 'lucide-react';
import { ChartCard } from '../components/charts';
import { chartsData } from '../data/chartsData';
import ChartCategorySidebar from '../components/layout/ChartCategorySidebar';
import { getChartLibrarySettings, putChartLibrarySettings, getDatabases } from '../services/api';
import { WriteGuard, ReadOnlyBanner } from '../components/auth/WriteGuard';
import '../utils/reportChartSetup';

export const ChartLibrary = () => {
    const { t } = useTranslation(['pages-misc-2', 'common']);
    const [activeCategory, setActiveCategory] = useState('All Examples');
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const databaseId = searchParams.get('database_id');
    const [databases, setDatabases] = useState([]);
    const [databasesLoading, setDatabasesLoading] = useState(true);
    const [reportSettings, setReportSettings] = useState({});
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState(null);
    const [saveState, setSaveState] = useState('idle');
    const navigate = useNavigate();

    const categories = [
        { name: 'All Examples', icon: LayoutGrid },
        { name: 'Line', icon: LineIcon },
        { name: 'Bar', icon: BarIcon },
        { name: 'Pie / Doughnut', icon: PieIcon },
        { name: 'Radar / Polar', icon: Radar },
        { name: 'Table', icon: Table2 },
        { name: 'Special Styles', icon: Layers },
    ];

    const filteredCharts = chartsData.filter(chart => {
        const matchesCategory = activeCategory === 'All Examples' || chart.category === activeCategory;
        const matchesSearch = chart.title.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    useEffect(() => {
        if (!databaseId) {
            setReportSettings({});
            setSettingsError(null);
            setSettingsLoading(false);
            return;
        }
        let cancelled = false;
        setSettingsLoading(true);
        setSettingsError(null);
        (async () => {
            try {
                const res = await getChartLibrarySettings(databaseId);
                const settings = res.data?.data?.settings ?? {};
                if (!cancelled) {
                    setReportSettings(typeof settings === 'object' && settings !== null ? settings : {});
                }
            } catch (e) {
                if (!cancelled) {
                    setSettingsError(e.response?.data?.detail || e.message || t('pages-misc-2:chartLibrary.failedToLoadSettings'));
                    setReportSettings({});
                }
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [databaseId, t]);

    useEffect(() => {
        let cancelled = false;
        setDatabasesLoading(true);
        (async () => {
            try {
                const res = await getDatabases();
                const list = res.data?.data?.databases ?? [];
                if (!cancelled) setDatabases(Array.isArray(list) ? list : []);
            } catch {
                if (!cancelled) setDatabases([]);
            } finally {
                if (!cancelled) setDatabasesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleDatabaseChange = useCallback(
        (e) => {
            const v = e.target.value;
            const next = new URLSearchParams(searchParams);
            if (v) next.set('database_id', v);
            else next.delete('database_id');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const handleReportNoteChange = useCallback((chartId, value) => {
        setReportSettings((prev) => ({ ...prev, [chartId]: value }));
    }, []);

    const handleSaveSettings = async () => {
        if (!databaseId) return;
        setSaveState('saving');
        setSettingsError(null);
        try {
            await putChartLibrarySettings(databaseId, { settings: reportSettings });
            setSaveState('saved');
            setTimeout(() => setSaveState('idle'), 2000);
        } catch (e) {
            setSaveState('error');
            setSettingsError(e.response?.data?.detail || e.message || t('pages-misc-2:chartLibrary.saveFailed'));
        }
    };

    const handleChartClick = (id) => {
        const qs = databaseId ? `?database_id=${encodeURIComponent(databaseId)}` : '';
        navigate(`/chart-library/${id}${qs}`);
    };

    return (
        <div className={`flex flex-col lg:flex-row h-full bg-gray-50 overflow-hidden font-sans`}>
            {/* Mobile Header Toggle */}
            <div className="lg:hidden bg-white border-b border-gray-100 p-4 flex items-center justify-between sticky top-0 z-20">
                <div className="flex items-center gap-2 text-primary-600 font-bold">
                    <LayoutGrid className="w-5 h-5" />
                    <span>{t('pages-misc-2:chartLibrary.categories')}</span>
                </div>
                <button 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 rounded-lg bg-gray-50 text-gray-500"
                >
                    {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {/* Sidebar with mobile state */}
            <div className={`
                fixed inset-0 z-30 lg:relative lg:inset-auto lg:block 
                ${isMobileMenuOpen ? 'block' : 'hidden'}
            `}>
                <div className="absolute inset-0 bg-black/20 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
                <div className="relative h-full w-72 bg-white shadow-xl lg:shadow-none">
                    <ChartCategorySidebar
                        categories={categories}
                        activeCategory={activeCategory}
                        setActiveCategory={(cat) => {
                            setActiveCategory(cat);
                            setIsMobileMenuOpen(false);
                        }}
                    />
                </div>
            </div>

            <main className="flex-1 overflow-y-auto">
                <header className={`p-theme flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white border-b border-gray-100 gap-4`}>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl lg:text-3xl font-black text-gray-900 tracking-tight mb-1 sm:mb-2">
                            {t('pages-misc-2:chartLibrary.title')}
                        </h1>
                        <p className="text-gray-500 text-xs sm:text-sm font-medium">
                            {t('pages-misc-2:chartLibrary.discoverCharts', { count: chartsData.length })}
                        </p>
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 max-w-xl">
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 shrink-0">
                                <Database className="w-4 h-4 text-primary-600" />
                                {t('pages-misc-2:chartLibrary.databaseForReportNotes')}
                            </label>
                            <select
                                value={databaseId || ''}
                                onChange={handleDatabaseChange}
                                disabled={databasesLoading}
                                className="w-full sm:flex-1 text-sm border border-gray-200 rounded-theme px-3 py-2 bg-white text-gray-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-500/15 disabled:opacity-60"
                            >
                                <option value="">
                                    {databasesLoading ? t('pages-misc-2:chartLibrary.loadingDatabases') : t('pages-misc-2:chartLibrary.selectDatabase')}
                                </option>
                                {databases.map((db) => (
                                    <option key={db.id} value={String(db.id)}>
                                        {db.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {databaseId && settingsLoading && (
                            <p className="text-gray-500 text-xs mt-2 flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                {t('pages-misc-2:chartLibrary.loadingSavedNotes')}
                            </p>
                        )}
                        {settingsError && (
                            <p className="text-red-600 text-xs mt-2">{settingsError}</p>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:items-center">
                        {databaseId && (
                            <WriteGuard>
                                <button
                                    type="button"
                                    onClick={handleSaveSettings}
                                    disabled={saveState === 'saving' || settingsLoading}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-theme bg-primary-600 text-white text-sm font-bold hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                >
                                    {saveState === 'saving' ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    {saveState === 'saved'
                                        ? t('pages-misc-2:chartLibrary.saved')
                                        : saveState === 'error'
                                          ? t('pages-misc-2:chartLibrary.retrySave')
                                          : t('pages-misc-2:chartLibrary.saveNotes')}
                                </button>
                            </WriteGuard>
                        )}
                        <div className="relative w-full sm:w-80 lg:w-96 group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                            <input
                                type="text"
                                placeholder={t('pages-misc-2:chartLibrary.searchChartsPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full pl-11 pr-4 py-2.5 sm:py-3 bg-gray-50 border border-transparent rounded-theme text-sm focus:bg-white focus:border-primary-200 focus:ring-4 focus:ring-primary-500/5 outline-none transition-all`}
                            />
                        </div>
                    </div>
                </header>

                <div className="p-theme">
                    <ReadOnlyBanner className="mb-4" />
                    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-min`}>
                        {filteredCharts.length > 0 ? (
                            filteredCharts.map((chart) => {
                                const isFullWidth =
                                    chart.type === 'DASHBOARD' ||
                                    chart.type === 'KPI' ||
                                    chart.type === 'TABLE';
                                return (
                                    <div
                                        key={chart.id}
                                        className={`${isFullWidth ? 'col-span-full' : ''}`}
                                    >
                                        <ChartCard
                                            chart={chart}
                                            onClick={handleChartClick}
                                            databaseId={databaseId || undefined}
                                            reportNote={reportSettings[chart.id] ?? ''}
                                            onReportNoteChange={databaseId ? handleReportNoteChange : undefined}
                                        />
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-span-full py-20 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
                                    <Search className="w-8 h-8" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{t('pages-misc-2:chartLibrary.noChartsFound')}</h3>
                                <p className="text-gray-500">{t('pages-misc-2:chartLibrary.tryAdjustingFilter')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};
